// Small formatting helpers for times, names, and ages. All times are shown in one zone
// (the office's, or the customer's when the text says so).

import { clockWords, formatPhone } from '@bt/core';

const ZONE_WORDS: Record<string, string> = {
  'America/New_York': 'Eastern',
  'America/Detroit': 'Eastern',
  'America/Kentucky/Louisville': 'Eastern',
  'America/Indiana/Indianapolis': 'Eastern',
  'America/Chicago': 'Central',
  'America/Indiana/Knox': 'Central',
  'America/Menominee': 'Central',
  'America/North_Dakota/Center': 'Central',
  'America/Denver': 'Mountain',
  'America/Boise': 'Mountain',
  'America/Phoenix': 'Arizona',
  'America/Los_Angeles': 'Pacific',
  'America/Anchorage': 'Alaska',
  'America/Juneau': 'Alaska',
  'Pacific/Honolulu': 'Hawaii',
  'America/Puerto_Rico': 'Atlantic',
};

/** "Central", "Eastern"; falls back to the zone's short name ("CST") or the raw id. */
export function zoneLabel(tz: string | null | undefined): string {
  if (!tz) return '';
  if (ZONE_WORDS[tz]) return ZONE_WORDS[tz];
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date());
    const name = parts.find((p) => p.type === 'timeZoneName')?.value;
    return name ?? tz;
  } catch {
    return tz;
  }
}

function safeDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmt(d: Date, tz: string | undefined, opts: Intl.DateTimeFormatOptions): string {
  try {
    return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: tz }).format(d);
  } catch {
    return new Intl.DateTimeFormat('en-US', opts).format(d);
  }
}

/** "3:42 PM" */
export function fmtTime(iso: string | null | undefined, tz?: string): string {
  const d = safeDate(iso);
  if (!d) return '';
  return fmt(d, tz, { hour: 'numeric', minute: '2-digit' });
}

/** "Thu 9/3" */
export function fmtShortDate(iso: string | null | undefined, tz?: string): string {
  const d = safeDate(iso);
  if (!d) return '';
  return fmt(d, tz, { weekday: 'short', month: 'numeric', day: 'numeric' });
}

/** "Thu 9/3 at 6:00 PM" */
export function fmtDateAtTime(iso: string | null | undefined, tz?: string): string {
  const d = safeDate(iso);
  if (!d) return '';
  return `${fmtShortDate(iso, tz)} at ${fmtTime(iso, tz)}`;
}

/** "Sep 3, 2026, 3:42 PM" */
export function fmtFull(iso: string | null | undefined, tz?: string): string {
  const d = safeDate(iso);
  if (!d) return '';
  return fmt(d, tz, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** "Today", "Yesterday", or "Thu 9/3" — for the day separators in a thread. */
export function dayLabel(iso: string, tz?: string, now = new Date()): string {
  const d = safeDate(iso);
  if (!d) return '';
  const key = (x: Date) => fmt(x, tz, { year: 'numeric', month: 'numeric', day: 'numeric' });
  const today = key(now);
  const yesterday = key(new Date(now.getTime() - 86400000));
  const k = key(d);
  if (k === today) return 'Today';
  if (k === yesterday) return 'Yesterday';
  return fmt(d, tz, { weekday: 'short', month: 'short', day: 'numeric', year: d.getFullYear() === now.getFullYear() ? undefined : 'numeric' });
}

/** Compact relative time for list rows: "now", "4m", "2h", "Tue", "9/1". */
export function fmtRelative(iso: string | null | undefined, tz?: string, now = new Date()): string {
  const d = safeDate(iso);
  if (!d) return '';
  const diff = now.getTime() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'now';
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return fmt(d, tz, { weekday: 'short' });
  return fmt(d, tz, { month: 'numeric', day: 'numeric' });
}

/** "16 min", "2 h 5 min", "3 days" — for heartbeat ages. */
export function fmtAgeSeconds(s: number | null | undefined): string {
  if (s == null || !Number.isFinite(s)) return 'never';
  if (s < 60) return `${Math.max(0, Math.round(s))} s`;
  const min = Math.floor(s / 60);
  if (min < 60) return `${min} min`;
  const hr = Math.floor(min / 60);
  const rem = min % 60;
  if (hr < 48) return rem ? `${hr} h ${rem} min` : `${hr} h`;
  return `${Math.floor(hr / 24)} days`;
}

/** "every 15 min" */
export function fmtEvery(s: number | null | undefined): string {
  if (s == null || !Number.isFinite(s) || s <= 0) return 'unknown';
  const min = Math.round(s / 60);
  return min < 1 ? `every ${Math.round(s)} s` : `every ${min} min`;
}

/** Seconds since an ISO time, or null. */
export function ageSeconds(iso: string | null | undefined, now = new Date()): number | null {
  const d = safeDate(iso);
  if (!d) return null;
  return Math.max(0, Math.round((now.getTime() - d.getTime()) / 1000));
}

export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Name if we have one, else the formatted number. */
export function displayName(c: { name?: string | null; phone_number: string } | null | undefined): string {
  if (!c) return 'Unknown';
  return c.name && c.name.trim() ? c.name : formatPhone(c.phone_number);
}

/** "08:00:00" or "08:00" -> "08:00" (for <input type=time>) */
export function hhmm(t: string | null | undefined): string {
  if (!t) return '';
  const m = /^(\d{1,2}):(\d{2})/.exec(t);
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : t;
}

/** "08:00" -> "8:00 AM". One helper for every screen: `clockWords` in core. */
export function hhmmWords(t: string | null | undefined): string {
  return clockWords(t);
}

/** Is a line paused right now? Handles 'infinity' (which JSON cannot carry) and a server `paused` flag. */
export function linePaused(line: { paused_until: string | null; paused?: boolean }): { paused: boolean; forever: boolean } {
  if (line.paused === true) {
    const ms = line.paused_until ? new Date(line.paused_until).getTime() : NaN;
    return { paused: true, forever: !Number.isFinite(ms) || new Date(ms).getFullYear() > 2100 };
  }
  const pu = line.paused_until;
  if (!pu) return { paused: false, forever: false };
  const ms = /infinity/i.test(pu) ? Infinity : new Date(pu).getTime();
  const paused = ms === Infinity || ms > Date.now();
  return { paused, forever: paused && (ms === Infinity || new Date(ms).getFullYear() > 2100) };
}

export function pluralize(n: number, one: string, many = one + 's'): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Make a readable temporary password (no ambiguous letters). */
export function makeTempPassword(len = 12): string {
  const alphabet = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const buf = new Uint32Array(len);
  crypto.getRandomValues(buf);
  let out = '';
  for (let i = 0; i < len; i++) out += alphabet[buf[i] % alphabet.length];
  return out;
}

export function uuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/**
 * "2h ago", "just now", "never". fmtRelative returns the bare word "now" for anything under a
 * minute, and "checked now ago" is not English, so every screen should use this instead.
 */
export function fmtAgo(iso: string | null | undefined, tz?: string, now = new Date()): string {
  const rel = fmtRelative(iso, tz, now);
  if (!rel) return 'never';
  return rel === 'now' ? 'just now' : `${rel} ago`;
}
